import React, { useState } from 'react';
import { DASHBOARD_TEXT } from '../../../../../../features/dashboard/infra/ui/constants/dashboardText';
import { GoSidebarCollapse, GoSidebarExpand } from 'react-icons/go';
import { useAuth } from '@/shared/auth';
import { LogOutIcon, Building2Icon } from 'lucide-react';
import useLogOut from '@/shared/infra/hooks/useLogout';
import { AccountSelectionDialog } from '@/features/auth/infra/ui/components/AccountSelectionDialog';

export interface DashboardHeaderProps {
  onSidebarToggle: () => void;
  showNotification?: boolean;
  sidebarCollapsed?: boolean;
}

export const DashboardHeader: React.FC<DashboardHeaderProps> = ({
  onSidebarToggle,
  showNotification = true,
  sidebarCollapsed,
}) => {
  const { user, account, role } = useAuth();
  const {handleLogOut} = useLogOut();
  const [orgSwitchDialogOpen, setOrgSwitchDialogOpen] = useState(false);
  // Normalized roleKey (VTK-1828), not the display string `vtAppRole` (broker sends `super_admin`).
  const isSuperAdmin = role === 'super_admin';
  return (
    <header className="flex items-center h-16 border-b bg-dark-blue">
      <button
        onClick={onSidebarToggle}
        className="p-4 ms-1 rounded hover:bg-gradient-secondary focus:outline-none cursor-pointer"
        aria-label="Toggle sidebar"
      >
        {/* Show sidebar SVG icon on smaller devices (md and below) */}
        <img
          src="/svg/Sidebar_icon.svg"
          alt="Open sidebar"
          width={28}
          height={28}
          className="md:hidden"
        />

        {/* Show collapse/expand icons on larger devices (md and above) */}
        {sidebarCollapsed ? (
          <GoSidebarCollapse size={24} color="white" className="hidden md:block" />
        ) : (
          <GoSidebarExpand size={24} color="white" className="hidden md:block" />
        )}
      </button>
      <h1 className="text-lg ms-4 text-start font-semibold flex-1 text-white">
        {DASHBOARD_TEXT.header.greeting(`${user?.firstName} ${user?.lastName}`)}
      </h1>
      {showNotification && (
        <button
          className="p-4 rounded hover:bg-gradient-secondary focus:outline-none cursor-pointer"
          aria-label="Notifications"
        >
          <img
            src="/svg/notification.svg"
            alt={DASHBOARD_TEXT.header.notifications}
            width={16}
            height={16}
          />
        </button>
      )}
      {isSuperAdmin && (
        <button
          onClick={() => setOrgSwitchDialogOpen(true)}
          className="p-4 rounded hover:bg-gradient-secondary focus:outline-none cursor-pointer"
          aria-label="Change Account"
          title="Change Account"
        >
          <Building2Icon size={20} color="white" />
        </button>
      )}
      <button
        className="p-2 px-4 rounded focus:outline-none"
        aria-label={DASHBOARD_TEXT.header.appLogoAlt}
      >
        <img
          src="/svg/VT_logo_dashboard_header.svg"
          alt={DASHBOARD_TEXT.header.appLogoAlt}
          width={32}
          height={18}
        />
      </button>
      <button
        className="p-2 px-4 rounded focus:outline-none"
        aria-label={DASHBOARD_TEXT.header.appLogoAlt}
        onClick={handleLogOut}
      >
        <LogOutIcon className="text-white cursor-pointer" />
      </button>

      {/* Switchable Organization Selection Dialog - Super Admin Only */}
      {isSuperAdmin && (
        <AccountSelectionDialog
          open={orgSwitchDialogOpen}
          mode="switchable"
          defaultSelectedAccountId={account?.id}
          onClose={() => setOrgSwitchDialogOpen(false)}
        />
      )}
    </header>
  );
};
